import { AnsweredQuestionType, QuizCtaType, QuizQuestionType, QuizResult, QuizStatus } from 'app/types'

const MIN_VALID_QUESTIONS = 1
const MIN_VALID_ANSWERS = 2

const hasText = (value: string): boolean => value?.trim()?.length > 0

export const getQuestionValidationMessage = (question: QuizQuestionType): string | null => {
    if (!hasText(question.text)) return "A question can't be empty"
    const filledAnswers = question.answers.filter(answer => hasText(answer.text))
    if (filledAnswers.length < MIN_VALID_ANSWERS) return `A question must have at least ${MIN_VALID_ANSWERS} answers`
    if (!filledAnswers.some(answer => answer.isCorrect)) return 'A question must have at least one correct answer'
    return null
}

export const isQuestionValid = (question: QuizQuestionType): boolean => getQuestionValidationMessage(question) === null

export const hasInvalidQuestion = (quiz: QuizCtaType): boolean =>
    quiz.questions.some(question => !isQuestionValid(question))

export const getDisplayableQuiz = (quiz: QuizCtaType): QuizCtaType | null => {
    const questions = quiz.questions
        .filter(isQuestionValid)
        .map(question => ({ ...question, answers: question.answers.filter(answer => hasText(answer.text)) }))

    if (questions.length < MIN_VALID_QUESTIONS) return null

    const passingScore = quiz.passingScore === null ? null : Math.min(quiz.passingScore, questions.length)

    return { ...quiz, questions, passingScore, requirePassToComplete: quiz.requirePassToComplete ?? false }
}

export const getQuizStatus = (quiz: QuizCtaType, quizResult: QuizResult): QuizStatus => {
    const { requirePassToComplete, passingScore } = quiz
    const { correctCount, totalQuestions } = quizResult

    if (correctCount === totalQuestions) return 'perfect'
    if (passingScore !== null && correctCount >= passingScore) return 'passed'
    if (passingScore !== null && requirePassToComplete && correctCount < passingScore) return 'almost_there'
    return 'completed'
}

const COMPLETE_STATUSES: Set<QuizStatus> = new Set<QuizStatus>(['passed', 'perfect', 'completed'])

export const isQuizComplete = (status?: QuizStatus | 'skipped'): boolean =>
    status !== undefined && status !== 'skipped' && COMPLETE_STATUSES.has(status)

const isAnsweredCorrectly = ({ correctAnswerIds, selectedAnswerIds }: AnsweredQuestionType) => {
    const correct = [...correctAnswerIds].sort()
    const selected = [...selectedAnswerIds].sort()

    return correct.length === selected.length && correct.every((id, index) => id === selected[index])
}

export const countCorrectAnswers = (answeredQuestions: Array<AnsweredQuestionType>): number =>
    answeredQuestions.filter(isAnsweredCorrectly).length

export const areRequiredQuizzesPassed = (requiredQuizIds: Array<string>, passedQuizIds: Set<string>): boolean =>
    requiredQuizIds.every(id => passedQuizIds.has(id))
